// step.js — Stufenlinie (z. B. Versionsverlauf) als SVG + versteckte Datentabelle.
// Teilt Achse, Raster und Datentabelle mit bars.js (Klassen vc-bars-*); Farben aus Tokens.
import { html, unsafeHtml, attrs } from '../core/h.js';

/**
 * Pfad einer Stufenlinie in einem w×h-Feld. values: Zahl oder null (kein Wert → Lücke).
 * Jeder Wert gilt vom Beginn seines Tages bis zum Beginn des nächsten; der letzte reicht bis zum rechten Rand.
 * Y-Bereich: min..max der Werte (gleiche Werte → Linie in der Mitte).
 */
export function stepLayout(values, { w = 600, h = 160 } = {}) {
  const v = (values || []).map((x) => (x == null || Number.isNaN(Number(x)) ? null : Number(x)));
  const nums = v.filter((x) => x != null);
  if (!nums.length) return { path: '', min: null, max: null };
  const min = Math.min(...nums), max = Math.max(...nums);
  const slot = w / v.length;
  const y = (x) => (max === min ? h / 2 : h - ((x - min) / (max - min)) * h);
  const f = (n) => n.toFixed(1);
  let path = '', prev = null;
  v.forEach((x, i) => {
    if (x == null) { prev = null; return; }
    if (prev == null) path += `${path ? ' ' : ''}M${f(i * slot)},${f(y(x))}`;
    else if (x !== prev) path += ` V${f(y(x))}`;
    path += ` H${f((i + 1) * slot)}`;
    prev = x;
  });
  // Folge-H-Befehle auf derselben Höhe zusammenfassen
  path = path.replace(/( H[\d.]+)+(?= V| M|$)/g, (m) => ' ' + m.trim().split(' ').at(-1));
  return { path, min, max };
}

/**
 * points: [{ label, value: number|null, note? }], fmt(value) → Text für Achse/Tabelle.
 * columns: Spaltenköpfe der versteckten Tabelle [Zeile, Wert, Notiz].
 */
export function stepLine({ points, ariaLabel, fmt = String, caption = '', columns = ['', '', ''], tick }) {
  const W = 600, H = 160;
  const { path, min, max } = stepLayout(points.map((p) => p.value), { w: W, h: H });
  // Beschriftung wöchentlich ab dem letzten Punkt (= heute); auf schmalem Platz nur jede zweite davon (is-minor)
  const every = tick || ((i, n) => (n - 1 - i) % 7 === 0);
  const nth = (i) => points.slice(i + 1).filter((_, k) => every(i + 1 + k, points.length)).length;
  const grid = [0, 0.5, 1].map((f) => `<line class="vc-bars-grid" x1="0" x2="${W}" y1="${(H * f).toFixed(1)}" y2="${(H * f).toFixed(1)}"/>`).join('');
  return html`<figure class="vc-bars vc-step" role="group" aria-label="${ariaLabel}">
    <div class="vc-bars-plot vc-step-plot">
      ${max != null ? html`<span class="vc-bars-ymax t-label-s t-muted" aria-hidden="true">${fmt(max)}</span>` : ''}
      ${max != null && min !== max ? html`<span class="vc-step-ymin t-label-s t-muted" aria-hidden="true">${fmt(min)}</span>` : ''}
      ${unsafeHtml(`<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true" focusable="false">${grid}<line class="vc-bars-axis" x1="0" x2="${W}" y1="${H}" y2="${H}"/>${path ? `<path class="vc-step-line" d="${path}"/>` : ''}</svg>`)}
    </div>
    <div class="vc-bars-x t-label-s t-muted" aria-hidden="true">${points.map((p, i) => { const on = every(i, points.length); return html`<span ${attrs({ class: on && nth(i) % 2 ? 'is-minor' : null })}>${on ? p.label : ''}</span>`; })}</div>
    ${caption ? html`<figcaption class="vc-bars-legend t-label-m t-muted">${caption}</figcaption>` : ''}
    <div class="vc-visually-hidden"><table><caption>${ariaLabel}</caption>
      <thead><tr>${columns.map((c) => html`<th scope="col">${c}</th>`)}</tr></thead>
      <tbody>${points.map((p) => html`<tr><th scope="row">${p.label}</th><td>${p.value == null ? '–' : fmt(p.value)}</td><td>${p.note || ''}</td></tr>`)}</tbody>
    </table></div>
  </figure>`;
}
